import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { VehicleCard } from "../components/VehicleCard";
import { Seo } from "../components/Seo";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Search, ShieldCheck, Sparkles, HandCoins, ArrowRight, MapPin, CheckCircle2, SlidersHorizontal, X } from "lucide-react";

const BRANDS = ["volkswagen", "renault", "peugeot", "bmw", "mercedes-benz", "audi", "citroen", "opel", "ford", "nissan", "toyota", "seat", "fiat", "dacia", "kia", "hyundai", "alfa-romeo"];
const SEGMENTS = ["Citadino", "Utilitário", "Familiar", "SUV", "Carrinha", "Monovolume", "Comercial", "Coupé"];

export default function Home() {
  const [vehicles, setVehicles] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [brand, setBrand] = useState("all");
  const [fuel, setFuel] = useState("all");
  const [segment, setSegment] = useState("all");
  const [yearMin, setYearMin] = useState("");
  const [yearMax, setYearMax] = useState("");
  const [kmMin, setKmMin] = useState("");
  const [kmMax, setKmMax] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [showAdv, setShowAdv] = useState(false);
  const clearFilters = () => { setSearch(""); setBrand("all"); setFuel("all"); setSegment("all"); setYearMin(""); setYearMax(""); setKmMin(""); setKmMax(""); setPriceMin(""); setPriceMax(""); };

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (brand !== "all") params.brand = brand;
    if (fuel !== "all") params.fuel = fuel;
    if (segment !== "all") params.segment = segment;
    if (yearMin) params.year_min = yearMin;
    if (yearMax) params.year_max = yearMax;
    if (kmMin) params.km_min = kmMin;
    if (kmMax) params.km_max = kmMax;
    if (priceMin) params.price_min = priceMin;
    if (priceMax) params.price_max = priceMax;
    const { data } = await api.get("/vehicles", { params });
    setVehicles(data);
    setLoading(false);
  }, [search, brand, fuel, segment, yearMin, yearMax, kmMin, kmMax, priceMin, priceMax]);

  useEffect(() => {
    api.get("/vehicles/brands").then((r) => setBrands(r.data));
    api.get("/vehicles", { params: { featured: true } }).then((r) => setFeatured(r.data));
  }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const scrollCatalog = () => document.getElementById("catalog")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="App">
      <Seo
        title="STAND LF — Automóveis usados em São João de Ovar"
        description="Stand de automóveis usados e seminovos em São João de Ovar. Viaturas revistas e com garantia, financiamento e retoma. Servimos Ovar, Aveiro e Porto."
        canonical="https://standlf.com/"
      />
      <Navbar />

      {/* HERO — dynamic, no stock car photos */}
      <section className="relative pt-[72px] overflow-hidden bg-zinc-950 text-white">
        {/* animated green glows */}
        <div className="pointer-events-none absolute -top-20 -left-24 w-[420px] h-[420px] rounded-full bg-primary/30 blur-[120px] animate-float" />
        <div className="pointer-events-none absolute top-40 right-0 w-[380px] h-[380px] rounded-full bg-primary/20 blur-[130px] animate-float-slow" />
        <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "34px 34px" }} />
        {/* faint brand logo decoration */}
        <img src="/logo-light.png" alt="" aria-hidden className="pointer-events-none absolute -bottom-10 right-4 w-[540px] max-w-[70%] opacity-[0.05] hidden md:block" />

        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-4 py-1.5 mb-7 animate-fade-up">
            <MapPin className="w-3.5 h-3.5 text-primary" />
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-white/80">São João de Ovar</span>
          </div>
          <h1 className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] max-w-4xl animate-fade-up" style={{ animationDelay: "80ms" }}>
            Encontre a sua<br /><span className="text-primary">próxima viatura.</span>
          </h1>
          <p className="text-white/70 text-base sm:text-lg mt-7 max-w-xl animate-fade-up" style={{ animationDelay: "160ms" }}>
            Viaturas selecionadas, revistas e prontas a estrear. Transparência total, do primeiro contacto à entrega das chaves.
          </p>
          <div className="flex flex-wrap gap-3 mt-9 animate-fade-up" style={{ animationDelay: "240ms" }}>
            <Button data-testid="hero-view-catalog" onClick={scrollCatalog} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-12 px-7 text-base transition-transform hover:-translate-y-0.5">
              Ver viaturas <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <a href="#contact">
              <Button variant="outline" className="rounded-full h-12 px-7 text-base font-semibold bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white transition-colors">
                Contactos
              </Button>
            </a>
          </div>

          {/* stats */}
          <div className="grid grid-cols-3 gap-6 sm:gap-10 mt-16 max-w-xl animate-fade-up" style={{ animationDelay: "320ms" }}>
            {[
              { n: `${vehicles.filter((v) => !v.sold).length}+`, l: "Viaturas em stock" },
              { n: "100%", l: "Revistas" },
              { n: "Garantia", l: "Incluída" },
            ].map((s) => (
              <div key={s.l}>
                <p className="font-heading font-black text-3xl sm:text-4xl text-primary tracking-tight">{s.n}</p>
                <p className="text-white/60 text-xs sm:text-sm mt-1">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 sm:grid-cols-3 gap-6 mt-16">
        {[
          { icon: ShieldCheck, t: "Garantia incluída", d: "Todas as viaturas com garantia e histórico verificado." },
          { icon: Sparkles, t: "Revisão completa", d: "Inspeção multiponto antes de qualquer entrega." },
          { icon: HandCoins, t: "Financiamento", d: "Soluções à medida com aprovação rápida." },
        ].map((f) => (
          <div key={f.t} className="bg-secondary/60 rounded-3xl p-7 border border-black/5 transition-transform hover:-translate-y-1">
            <f.icon className="w-8 h-8 text-primary mb-4" />
            <h3 className="font-heading font-bold text-lg">{f.t}</h3>
            <p className="text-muted-foreground text-sm mt-1.5">{f.d}</p>
          </div>
        ))}
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section id="featured" className="max-w-7xl mx-auto px-5 sm:px-8 pt-24">
          <div className="flex items-end justify-between gap-4 mb-10">
            <div>
              <span className="text-primary font-bold text-xs tracking-[0.3em] uppercase">Selecionadas</span>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight mt-2">Viaturas em destaque</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featured.map((v, i) => <VehicleCard key={v.id} vehicle={v} index={i} />)}
          </div>
        </section>
      )}

      {/* CATALOG */}
      <section id="catalog" className="max-w-7xl mx-auto px-5 sm:px-8 pt-24">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <span className="text-primary font-bold text-xs tracking-[0.3em] uppercase">Catálogo</span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight mt-2">As nossas viaturas</h2>
          </div>
          <p className="text-muted-foreground text-sm">{vehicles.filter((v) => !v.sold).length} viatura(s) disponível(is)</p>
        </div>

        <div className="mb-12 bg-white rounded-2xl border border-black/5 p-4 shadow-sm">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input data-testid="filter-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Pesquisar marca ou modelo..." className="pl-11 h-12 rounded-xl border-0 bg-secondary/60 focus-visible:ring-primary" />
            </div>
            <Select value={brand} onValueChange={setBrand}>
              <SelectTrigger data-testid="filter-brand" className="md:w-44 h-12 rounded-xl border-0 bg-secondary/60"><SelectValue placeholder="Marca" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as marcas</SelectItem>
                {brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={fuel} onValueChange={setFuel}>
              <SelectTrigger data-testid="filter-fuel" className="md:w-40 h-12 rounded-xl border-0 bg-secondary/60"><SelectValue placeholder="Combustível" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Combustível</SelectItem>
                {["Gasolina", "Diesel", "Híbrido", "Elétrico", "GPL"].map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button data-testid="filter-toggle" variant="outline" onClick={() => setShowAdv(!showAdv)} className="h-12 rounded-xl border-2 font-semibold shrink-0"><SlidersHorizontal className="w-4 h-4 mr-2" /> Filtros</Button>
          </div>

          {showAdv && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 pt-4 border-t border-black/5">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Segmento</label>
                <Select value={segment} onValueChange={setSegment}>
                  <SelectTrigger data-testid="filter-segment" className="mt-1.5 h-11 rounded-xl bg-secondary/60 border-0"><SelectValue placeholder="Segmento" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os segmentos</SelectItem>
                    {SEGMENTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Ano</label>
                <div className="flex gap-2 mt-1.5">
                  <Input data-testid="filter-year-min" type="number" value={yearMin} onChange={(e) => setYearMin(e.target.value)} placeholder="De" className="h-11 rounded-xl bg-secondary/60 border-0" />
                  <Input data-testid="filter-year-max" type="number" value={yearMax} onChange={(e) => setYearMax(e.target.value)} placeholder="Até" className="h-11 rounded-xl bg-secondary/60 border-0" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Quilómetros</label>
                <div className="flex gap-2 mt-1.5">
                  <Input data-testid="filter-km-min" type="number" value={kmMin} onChange={(e) => setKmMin(e.target.value)} placeholder="De" className="h-11 rounded-xl bg-secondary/60 border-0" />
                  <Input data-testid="filter-km-max" type="number" value={kmMax} onChange={(e) => setKmMax(e.target.value)} placeholder="Até" className="h-11 rounded-xl bg-secondary/60 border-0" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Preço (€)</label>
                <div className="flex gap-2 mt-1.5">
                  <Input data-testid="filter-price-min" type="number" value={priceMin} onChange={(e) => setPriceMin(e.target.value)} placeholder="De" className="h-11 rounded-xl bg-secondary/60 border-0" />
                  <Input data-testid="filter-price-max" type="number" value={priceMax} onChange={(e) => setPriceMax(e.target.value)} placeholder="Até" className="h-11 rounded-xl bg-secondary/60 border-0" />
                </div>
              </div>
              <div className="sm:col-span-2 lg:col-span-4 flex justify-end">
                <Button data-testid="filter-clear" variant="ghost" onClick={clearFilters} className="rounded-full font-semibold text-muted-foreground"><X className="w-4 h-4 mr-1.5" /> Limpar filtros</Button>
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => <div key={i} className="aspect-[4/3] rounded-3xl bg-muted animate-pulse" />)}
          </div>
        ) : vehicles.length === 0 ? (
          <div data-testid="empty-catalog" className="text-center py-24 bg-secondary/40 rounded-3xl">
            <p className="font-heading font-bold text-xl">Sem viaturas disponíveis</p>
            <p className="text-muted-foreground mt-2">Ajuste os filtros ou volte em breve.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {vehicles.map((v, i) => <VehicleCard key={v.id} vehicle={v} index={i} />)}
          </div>
        )}
      </section>

      {/* ABOUT — no stock photo, branded panel */}
      <section id="about" className="max-w-7xl mx-auto px-5 sm:px-8 pt-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-stretch">
          <div className="flex flex-col justify-center">
            <span className="text-primary font-bold text-xs tracking-[0.3em] uppercase">Sobre nós</span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight mt-3">O Stand LF</h2>
            <p className="text-muted-foreground text-base leading-relaxed mt-5">
              Somos um stand automóvel sediado em São João de Ovar, dedicado à venda de viaturas usadas e seminovas com a máxima qualidade. Cada carro passa por uma inspeção rigorosa antes de chegar ao nosso espaço.
            </p>
            <p className="text-muted-foreground text-base leading-relaxed mt-4">
              Acompanhamos cada cliente de forma próxima e transparente, ajudando a encontrar a viatura certa e as melhores condições de financiamento.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-8">
              {["Viaturas com garantia", "Financiamento à medida", "Retoma do seu usado", "Acompanhamento pós-venda"].map((t) => (
                <div key={t} className="flex items-center gap-2.5 text-sm font-medium">
                  <CheckCircle2 className="w-5 h-5 text-primary shrink-0" /> {t}
                </div>
              ))}
            </div>
          </div>

          <div className="relative rounded-3xl bg-zinc-950 text-white p-10 overflow-hidden">
            <div className="pointer-events-none absolute -top-16 -right-16 w-72 h-72 rounded-full bg-primary/25 blur-[90px] animate-float" />
            <div className="relative z-10">
              <img src="/logo-light.png" alt="STAND LF" className="h-14 w-auto mb-8" />
              <p className="font-heading font-bold text-2xl leading-snug tracking-tight">
                A sua próxima viatura começa com uma conversa de confiança.
              </p>
              <p className="text-white/60 mt-4 leading-relaxed">
                Visite-nos no nosso espaço em São João de Ovar ou fale connosco por telefone ou WhatsApp. Teremos todo o gosto em ajudar.
              </p>
              <a href="#contact">
                <Button className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-11 px-6 mt-8 transition-transform hover:-translate-y-0.5">
                  Falar connosco <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* BRANDS */}
      <section id="marcas" className="pt-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 text-center">
          <span className="text-primary font-bold text-xs tracking-[0.3em] uppercase">Marcas</span>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight mt-2">As marcas que comercializamos</h2>
          <p className="text-muted-foreground mt-3 max-w-xl mx-auto">Trabalhamos com as marcas mais procuradas do mercado, sempre com viaturas revistas e com garantia.</p>
        </div>
        <div className="mt-12 bg-secondary/50 border-y border-black/5 py-10 relative overflow-hidden">
          <div className="flex items-center gap-16 w-max animate-marquee">
            {[...BRANDS, ...BRANDS].map((b, i) => (
              <img
                key={`${b}-${i}`}
                src={`/brands/${b}.png`}
                alt={b}
                loading="lazy"
                className="h-12 sm:h-14 w-auto object-contain shrink-0 opacity-70 hover:opacity-100 transition-opacity"
              />
            ))}
          </div>
          <div className="pointer-events-none absolute inset-y-0 left-0 w-20 sm:w-32 bg-gradient-to-r from-secondary/90 to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-20 sm:w-32 bg-gradient-to-l from-secondary/90 to-transparent" />
        </div>
      </section>

      <div className="h-24" />
      <Footer />
    </div>
  );
}
